import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { securePut, secureGet, securePost } from '../../config/api';
import { SaveCancel } from '../../components/PrimaryButton';
import { useHistory } from 'react-router-dom';
import { formatAmount } from "../../utils/format";
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { useLocation } from 'react-router-dom';
import { PrimaryButton } from '../../components/PrimaryButton';
import Input from '../../components/Input';
import SnackbarNotification from '../../components/SnackbarNotification';
import LoadingLogo from '../../components/LoadingLogo';
import GeneratePopup from "../../components/GeneratePopUp";
import { PAYMENT_ENUMS } from "../../utils/paymentModes";
import discountIcon from '../../icons/discount.svg';

import plusIcon from '../../icons/plus.svg';
import DeleteIcon from '../../icons/trashR.svg';
import plus from '../../icons/plusP.svg';
import minus from '../../icons/minusP.svg';
import plusY from '../../icons/plusY.svg';
import minusY from '../../icons/minusY.svg';
import EyeIcon from '../../icons/eyeB.svg';
import xIcon from '../../icons/x.svg';
import loaderIcon from '../../icons/loaderO.svg';
import checkIcon from '../../icons/checkGr.svg'
import repairIcon from '../../icons/repair.svg';
const QuoteForm = ({
    title,
    baseFields = [],
    listFields = [],
    products = [],
    services = [],
    typeOptions = [],
    onSuccess,
    onCancel,
    initialValues = {},
    onRevisionChange, // Callback to expose revision change handler
}) => {
    const [formValues, setFormValues] = useState({});
    const [listEntities, setListEntities] = useState([]);
    const [currentEntity, setCurrentEntity] = useState({});
    const [totalHT, setTotalHT] = useState(0);
    const [totalTTC, setTotalTTC] = useState(0);
    const [errorMessage, setErrorMessage] = useState("");
    const history = useHistory();
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const id = location.state?.id;
    const [quote, setQuote] = useState(null);


    const [isPopupOpen, setPopupOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingPrice, setEditingPrice] = useState(null); // { index, value: string } when user is typing in a price input
    const [validatingPrice, setValidatingPrice] = useState(null); // { index, value: string } when SC is editing a proposed price before validating
    const [pendingItemEdits, setPendingItemEdits] = useState({}); // { [deliveryNoteDetailId]: { name, unitPrice } } - SC's edits before integrating
    // Snapshot of {listEntities, formValues} taken right after an "Intégrer au devis" - as long as
    // nothing changed since, there's nothing new to save (see integrateStorekeeperItem).
    const [postIntegrationSnapshot, setPostIntegrationSnapshot] = useState(null);
    const integratingSnapshotPendingRef = useRef(false);

    const showSnackbar = (message, severity = 'success', manual = false) => {
        setSnackbar({ open: true, message, severity, manual });
    };

    useEffect(() => {
        const checkDataReadiness = () => {
            const baseFieldsReady = baseFields.every(field =>
                !field.options || field.options.length > 0
            );

            if (id) {
                return quote && baseFieldsReady;
            }
            return baseFieldsReady;
        };

        if (checkDataReadiness()) {
            setLoading(false);
        } else {
            setLoading(true);
        }
    }, [quote, baseFields, id]);

    const fetchQuote = useCallback(async (signal) => {
        if (!id) return;
        try {
            const response = await secureGet(`/quotes/${id}`, showSnackbar, { signal });
            setQuote(response.data);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error("Failed to fetch quote:", error);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        // If initialValues contains a quote object (has id and quote-specific fields), use it directly
        if (initialValues && initialValues.id && (initialValues.details !== undefined || initialValues.client !== undefined || initialValues.quoteNumber !== undefined)) {
            setQuote(initialValues);
            console.log("quote", initialValues)

            return;
        }

        // Otherwise, fetch quote using id from location.state
        const abortController = new AbortController();
        fetchQuote(abortController.signal);
        return () => {
            abortController.abort();
        };
    }, [id, initialValues, fetchQuote]);

    useEffect(() => {
        if (quote) {
            const formInitialValues = {
                clientId: initialValues?.clientId || quote.client?.id,
                clientName: initialValues?.clientName || quote.client?.name || "",
                repoId: quote.repo?.id || "",
                advancePayment: quote.advancePayment || 0,
                title: quote.title || "",
            };
            setFormValues(formInitialValues);


            const productList = quote.details?.map((detail) => {
                return {
                    detailId: detail.id,
                    product: detail.product,
                    quantity: detail.quantity,
                    decimalQuantity: detail.decimalQuantity != null ? parseFloat(detail.decimalQuantity) : null,
                    discount: detail.salesDiscount || 0,
                    // Always carry the backend's own unitPrice/vatRate across, not just for free
                    // lines - a line integrated from a storekeeper's delivery note has a product
                    // but its price is the one from the BL (possibly SC-edited), not the catalog's.
                    unitPrice: detail.unitPrice != null ? parseFloat(detail.unitPrice) : null,
                    vatRate: detail.vatRate != null ? parseFloat(detail.vatRate) : null,
                    ...(isProductEntity(detail) ? {
                        // Free line, or a line integrated from a storekeeper's delivery note (has a
                        // real product, but is submitted like a free line - see handleSubmit): seed
                        // designation from the catalog product's own name so it's never blank, while
                        // staying a free-text input the SC can still rename.
                        designation: detail.mappedProductName || detail.designation || detail.product?.name || "",
                        notForProcurement: !!detail.notForProcurement,
                        priceStatus: detail.priceStatus || 'NONE',
                        proposedPrice: detail.proposedPrice != null ? parseFloat(detail.proposedPrice) : null,
                    } : {}),
                };
            }) || [];
            setListEntities(productList);

            if (integratingSnapshotPendingRef.current) {
                setPostIntegrationSnapshot(JSON.stringify({ productList, formInitialValues }));
                integratingSnapshotPendingRef.current = false;
            }
        } else if (initialValues && initialValues.receptionDocumentId && !quote) {
            // For new quotes, set receptionDocumentId, clientId (for payload), and clientName (for display)
            setFormValues((prev) => ({
                ...prev,
                receptionDocumentId: initialValues.receptionDocumentId,
                clientId: initialValues.clientId || prev.clientId, // For payload
                clientName: initialValues.clientName || prev.clientName || "", // For display
            }));
        } else if (initialValues?.clientId && !quote) {
            // Always pre-fill clientId (for payload) and clientName (for display) from initialValues
            setFormValues((prev) => ({
                ...prev,
                clientId: initialValues.clientId, // For payload
                clientName: initialValues.clientName || "", // For display
            }));
        }
    }, [quote, initialValues]);

    // Nothing to save right after an integration until the SC actually changes something -
    // disable Enregistrer rather than let it spawn an empty extra revision.
    const nothingChangedSinceIntegration = useMemo(() => {
        if (!postIntegrationSnapshot) return false;
        return JSON.stringify({ productList: listEntities, formInitialValues: formValues }) === postIntegrationSnapshot;
    }, [postIntegrationSnapshot, listEntities, formValues]);

    useEffect(() => {
        const calculatedTotalHT = listEntities.reduce(
            (sum, entity) => {
                const unitPriceHT = (entity.unitPrice != null && entity.unitPrice !== undefined)
                    ? entity.unitPrice
                    : (entity.product?.priceHT ?? 0);
                const productDiscount = entity.product?.discount || 0;
                const quoteDiscount = entity.discount || 0;
                const priceAfterProductDiscount = unitPriceHT * (1 - productDiscount / 100);
                const finalPrice = priceAfterProductDiscount * (1 - quoteDiscount / 100);
                return sum + getEffectiveQuantity(entity) * finalPrice;
            },
            0
        );

        const calculatedTotalTTC = listEntities.reduce(
            (sum, entity) => {
                const unitPriceHT = (entity.unitPrice != null && entity.unitPrice !== undefined)
                    ? entity.unitPrice
                    : (entity.product?.priceHT ?? 0);
                const isProductLine = isProductEntity(entity);
                const vatRate = isProductLine && (entity.vatRate != null && entity.vatRate !== undefined)
                    ? entity.vatRate / 100
                    : 0.20;
                const unitPriceTTC = unitPriceHT * (1 + vatRate);
                const productDiscount = entity.product?.discount || 0;
                const quoteDiscount = entity.discount || 0;
                const priceAfterProductDiscount = unitPriceTTC * (1 - productDiscount / 100);
                const finalPrice = priceAfterProductDiscount * (1 - quoteDiscount / 100);
                return sum + getEffectiveQuantity(entity) * finalPrice;
            },
            0
        );

        setTotalHT(calculatedTotalHT.toFixed(2));
        setTotalTTC(calculatedTotalTTC.toFixed(2));
    }, [listEntities]);

    const handleInputChange = (name, value) => {
        setFormValues((prev) => ({ ...prev, [name]: value }));
    };

    const removeListEntity = (index) => {
        setListEntities((prevEntities) =>
            prevEntities.filter((_, i) => i !== index)
        );
    };

    // Check if an entity is a service (not a product)
    const isService = (entity) => {
        return entity?.product?.family?.familyType === "SERVICE";
    };

    // Check if an entity is a product line (free line, or a catalog product that isn't a SERVICE -
    // e.g. a line integrated from a storekeeper's delivery note). Note: now that unitPrice/vatRate
    // are populated for every line (not just free ones), this must key off family type alone.
    const isProductEntity = (entity) => {
        return entity.product?.family?.familyType !== "SERVICE";
    };

    // Service (M.O.) lines may carry a fractional decimalQuantity (e.g. 2,5 heures) - authoritative
    // over the integer quantity when present, for price/total calculations.
    const getEffectiveQuantity = (entity) => (
        entity.decimalQuantity != null ? entity.decimalQuantity : (entity.quantity || 0)
    );

    const handleTrashClick = (index) => {
        removeListEntity(index);
    };

    const handleDiscountChange = (index, change) => {
        setListEntities((prevEntities) =>
            prevEntities.map((entity, i) => {
                if (i === index) {
                    return {
                        ...entity,
                        discount: Math.max(0,
                            parseFloat((entity.discount + change).toFixed(1))
                        )
                    };
                }
                return entity;
            })
        );
    };

    const handleUnitPriceChange = (index, value, discountInfo) => {
        const normalized = String(value).replace(',', '.');
        const num = normalized === '' ? null : parseFloat(normalized);
        if (num !== null && (Number.isNaN(num) || num < 0)) return;
        setListEntities((prevEntities) =>
            prevEntities.map((entity, i) => {
                if (i !== index) return entity;
                let unitPrice = num;
                if (num !== null && discountInfo) {
                    const { productDiscount = 0, quoteDiscount = 0 } = discountInfo;
                    const multiplier = (1 - productDiscount / 100) * (1 - quoteDiscount / 100);
                    unitPrice = multiplier > 0 ? num / multiplier : num;
                }
                return { ...entity, unitPrice: unitPrice ?? undefined };
            })
        );
    };

    // Service (M.O.) quantity: accepts a French-comma decimal (e.g. "2,5") and stores it separately
    // from the integer quantity used by product/free lines.
    const handleQuantityChange = (index, value) => {
        const normalized = String(value).replace(',', '.');
        const num = normalized === '' ? null : parseFloat(normalized);
        if (num !== null && (Number.isNaN(num) || num < 0)) return;
        setListEntities((prevEntities) =>
            prevEntities.map((entity, i) => (i === index ? { ...entity, decimalQuantity: num ?? undefined } : entity))
        );
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        // In modify mode, listFields are not required if listEntities already exist
        const fieldsToValidate = listEntities.length > 0
            ? [...baseFields]
            : [...baseFields, ...listFields];

        const missing = fieldsToValidate.filter(f => f.required && !formValues[f.name]);
        if (missing.length > 0) {
            showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
            return;
        }

        // Build payload according to backend format (removing items = new version only, no credit note)
        const currentDetails = listEntities.map((entity) => {
            const isProductLine = isProductEntity(entity);
            const unitPriceValue = isProductLine
                ? (entity.unitPrice != null ? parseFloat(entity.unitPrice) : null)
                : parseFloat(entity.product?.priceHT ?? 0);
            return {
                id: entity.detailId ? parseInt(entity.detailId, 10) : null,
                quantity: isProductLine ? (parseInt(entity.quantity, 10) || 0) : null,
                decimalQuantity: (!isProductLine && entity.decimalQuantity != null) ? parseFloat(entity.decimalQuantity) : null,
                salesDiscount: parseFloat(entity.discount || 0),
                freeLine: isProductLine,
                productId: isProductLine ? null : (parseInt(entity.product?.id, 10) || null),
                unitPrice: unitPriceValue,
                vatRate: parseFloat(isProductLine ? (entity.vatRate ?? 0) : 20),
                designation: (entity.designation != null && entity.designation !== "") ? String(entity.designation) : "",
                notForProcurement: isProductLine ? Boolean(entity.notForProcurement) : false,
            };
        }).filter(detail => detail.freeLine || detail.productId > 0);

        const payload = {
            clientId: parseInt(formValues.clientId, 10) || 0,
            advancePayment: parseFloat(formValues.advancePayment || 0),
            title: formValues.title || "",
            ...(formValues.repoId && { repoId: parseInt(formValues.repoId, 10) }),
            ...(initialValues?.receptionDocumentId && { receptionDocumentId: parseInt(initialValues.receptionDocumentId, 10) }),
            ...(quote?.id && { quoteId: parseInt(quote.id, 10) }),
            details: currentDetails,
        };


        try {
            setSubmitting(true);
            console.log("Submitting quote with payload:", payload);
            await securePut("/quotes/add", payload, showSnackbar);
            onSuccess();
            setFormValues({});
            setListEntities([]);
        } catch (error) {
            console.error("Failed to submit quote:", error);
        } finally {
            setSubmitting(false);
        }
    };



    const addListEntity = () => {
        const { type, product, quantity, designation, unitPrice, vatRate } = currentEntity;
        if (!type) {
            showSnackbar(t('placeholder.empty_fields'), "error");
            return;
        }

        const isProductType = type === 'products';
        const isServiceType = type === 'services';

        if (isProductType) {
            if (designation == null || designation === '' || quantity == null || quantity <= 0 ||
                vatRate == null) {
                showSnackbar(t('placeholder.empty_fields'), "error");
                return;
            }
        } else if (isServiceType) {
            const parsedQuantity = parseFloat(String(quantity).replace(',', '.'));
            if (!product || quantity == null || quantity === '' || Number.isNaN(parsedQuantity) || parsedQuantity <= 0) {
                showSnackbar(t('placeholder.empty_fields'), "error");
                return;
            }
        } else {
            return;
        }

        if (isServiceType) {
            const selectedItem = services.find((p) => p.value === product);
            if (!selectedItem) {
                showSnackbar('Service introuvable', "error");
                return;
            }
            const productId = selectedItem.fullEntity?.id;
            // M.O. quantity may be a French-comma decimal (e.g. "2,5" heures).
            const newQuantity = parseFloat(String(quantity).replace(',', '.'));
            const existingIndex = listEntities.findIndex((entity) => entity.product?.id === productId);

            if (existingIndex >= 0) {
                const existingEntity = listEntities[existingIndex];
                setListEntities((prev) => {
                    const newEntities = [...prev];
                    newEntities[existingIndex] = {
                        ...existingEntity,
                        decimalQuantity: getEffectiveQuantity(existingEntity) + newQuantity,
                    };
                    return newEntities;
                });
            } else {
                setListEntities((prev) => [...prev, {
                    product: selectedItem.fullEntity,
                    decimalQuantity: newQuantity,
                    discount: 0,
                }]);
            }
        } else {
            const newQuantity = parseInt(quantity, 10);
            const newEntity = {
                product: null,
                quantity: newQuantity,
                discount: 0,
                designation: designation ?? "",
                unitPrice: unitPrice != null && unitPrice !== '' ? parseFloat(unitPrice) : null,
                vatRate: vatRate != null && vatRate !== '' ? parseFloat(vatRate) : 0,
                notForProcurement: Boolean(currentEntity.notForProcurement),
                priceStatus: 'NONE',
                proposedPrice: null,
            };
            setListEntities((prev) => [...prev, newEntity]);
        }

        setCurrentEntity({});
    };

    const [propositionActionIndex, setPropositionActionIndex] = useState(null);

    const requestProposition = async (index) => {
        const entity = listEntities[index];
        setPropositionActionIndex(index);
        try {
            if (entity.detailId) {
                await securePost(`/quotes/details/${entity.detailId}/proposition`, {}, showSnackbar);
                setListEntities((prev) => prev.map((e, i) => i === index ? { ...e, priceStatus: 'PENDING' } : e));
                return;
            }
            if (!quote?.id) {
                showSnackbar("Veuillez d'abord enregistrer le devis avant de demander une proposition.", "error");
                return;
            }
            // Free line was just added in this session and has no detailId yet - the quote
            // itself already exists, so create+persist just this line instead of requiring
            // a full quote save first.
            const payload = {
                freeLine: true,
                designation: entity.designation || "",
                quantity: parseInt(entity.quantity, 10) || 0,
                vatRate: parseFloat(entity.vatRate ?? 20),
                notForProcurement: Boolean(entity.notForProcurement),
            };
            const response = await securePost(`/quotes/${quote.id}/details/proposition`, payload, showSnackbar);
            const created = response.data;
            setListEntities((prev) => prev.map((e, i) => i === index
                ? { ...e, detailId: created.id, priceStatus: created.priceStatus || 'PENDING' }
                : e));
        } catch (error) {
            console.error("Failed to request proposition:", error);
        } finally {
            setPropositionActionIndex(null);
        }
    };

    const validateProposition = async (index) => {
        const entity = listEntities[index];
        const rawValue = validatingPrice?.index === index ? validatingPrice.value : entity.proposedPrice;
        const finalPrice = parseFloat(String(rawValue ?? '').replace(',', '.'));
        if (Number.isNaN(finalPrice) || finalPrice < 0) {
            showSnackbar(t('placeholder.empty_fields'), "error");
            return;
        }
        setPropositionActionIndex(index);
        try {
            await securePut(`/quotes/details/${entity.detailId}/proposition/validate`, { finalPrice }, showSnackbar);
            setListEntities((prev) => prev.map((e, i) => i === index ? { ...e, priceStatus: 'VALIDATED', unitPrice: finalPrice } : e));
            setValidatingPrice(null);
        } catch (error) {
            console.error("Failed to validate proposition:", error);
        } finally {
            setPropositionActionIndex(null);
        }
    };



    const [integratingId, setIntegratingId] = useState(null);

    const getPendingItemEdit = (item) => pendingItemEdits[item.deliveryNoteDetailId] || {
        name: item.product?.name ?? '',
        unitPrice: item.unitPrice != null ? String(item.unitPrice) : '',
    };

    const updatePendingItemEdit = (item, field, value) => {
        setPendingItemEdits((prev) => ({
            ...prev,
            [item.deliveryNoteDetailId]: { ...getPendingItemEdit(item), [field]: value },
        }));
    };

    const integrateStorekeeperItem = async (item) => {
        if (!quote?.id) return;
        setIntegratingId(item.deliveryNoteDetailId);
        try {
            const edit = getPendingItemEdit(item);
            const payload = {
                name: edit.name,
                unitPrice: edit.unitPrice !== '' ? Number(edit.unitPrice) : null,
            };
            // The backend snapshots this as a new quote revision (for traceability) and returns
            // it directly - update local state with it rather than refetching by the (now stale)
            // previous quote id.
            integratingSnapshotPendingRef.current = true;
            const response = await securePost(`/quotes/${quote.id}/details/integrate/${item.deliveryNoteDetailId}`, payload, showSnackbar);
            setQuote(response.data);
            setPendingItemEdits((prev) => {
                const next = { ...prev };
                delete next[item.deliveryNoteDetailId];
                return next;
            });
        } catch (error) {
            integratingSnapshotPendingRef.current = false;
            console.error("Failed to integrate storekeeper item into quote:", error);
        } finally {
            setIntegratingId(null);
        }
    };

    console.log("listEntities", listEntities);

    return (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
            <div className="bg-[#F9FAFC] rounded-[12px] shadow-lg w-[75%] h-[760px] flex flex-col">
                <Helmet>
                    <title>Drivnest - {t("titles.quotes")}</title>
                </Helmet>

                {/* Header with title and close button */}
                <div className="p-6 pb-0">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-[32px] text-[#38485C] font-semibold">{title}</h2>
                    </div>
                </div>

                {/* Content */}
                <div className="px-8 overflow-hidden flex-1 flex flex-col">
                    {(
                        <form className="flex flex-col h-full">
                            {/* Base Fields - Fixed */}
                            <div className="mb-4">
                                <div className="grid grid-cols-3 gap-4">
                                    {baseFields.map((field, index) => {
                                        // For clientId field (text field), display clientName but payload uses clientId
                                        let displayValue = formValues[field.name] || "";
                                        if (field.name === 'clientId') {
                                            // Display clientName from initialValues or formValues
                                            displayValue = formValues.clientName || initialValues?.clientName || "";
                                        }

                                        return (
                                            <div key={index}>
                                                <Input
                                                    type={field.type || "text"}
                                                    label={field.label}
                                                    name={field.name}
                                                    value={displayValue}
                                                    options={field.options || []}
                                                    required={field.required}
                                                    disabled={field.name === 'clientId'}
                                                    onChange={(value) => {
                                                        if (field.name === 'clientId') {
                                                            // Update clientName for display, but keep clientId from initialValues for payload
                                                            setFormValues(prev => ({
                                                                ...prev,
                                                                clientName: value,
                                                                // Always preserve clientId from initialValues for payload
                                                                clientId: initialValues?.clientId || prev.clientId,
                                                            }));
                                                        } else {
                                                            handleInputChange(field.name, value);
                                                        }
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                {errorMessage && (
                                    <p className="mt-2 text-sm text-red-500 font-semibold">{errorMessage}</p>
                                )}
                            </div>

                            {/* List Fields - Fixed */}
                            {listFields.length > 0 && (
                                <div className="mb-4">
                                    <h3 className="text-[16px] font-semibold text-[#ACAFC6]">{t(`fields_types.products`)}</h3>
                                    <div className="flex items-end gap-3 mt-2">
                                        <div className="grid grid-cols-5 gap-4 flex-1">
                                            {listFields
                                                .filter((field) => !field.showCondition || currentEntity.type === field.showCondition)
                                                .map((field, index) => {
                                                    let fieldLabel = field.label;
                                                    if (field.name === 'product') {
                                                        fieldLabel = currentEntity.type === 'services'
                                                            ? t(`fields.list.service`)
                                                            : t(`fields.list.product`);
                                                    }

                                                    return (
                                                        <Input
                                                            key={index}
                                                            type={field.type || 'text'}
                                                            label={fieldLabel}
                                                            name={field.name}
                                                            value={currentEntity[field.name] || ''}
                                                            required={field.required}
                                                            onChange={(value) => {
                                                                if (field.name === 'type') {
                                                                    setCurrentEntity({
                                                                        ...currentEntity,
                                                                        type: value,
                                                                        product: '',
                                                                    });
                                                                } else {
                                                                    setCurrentEntity((prev) => ({ ...prev, [field.name]: value }));
                                                                }
                                                            }}
                                                            options={field.name === 'product'
                                                                ? (currentEntity.type === 'services' ? services : products).map((item) => ({
                                                                    value: item.value,
                                                                    name: item.name,
                                                                    reference: item.reference,
                                                                    fullEntity: item.fullEntity,
                                                                }))
                                                                : field.name === 'type'
                                                                    ? typeOptions
                                                                    : field.options || []}
                                                        />
                                                    );
                                                })}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={addListEntity}
                                            className="bg-[#043F88] text-white w-[48px] h-[48px] rounded-md hover:bg-[#3F3456] flex items-center justify-center"
                                        >
                                            <img src={plusIcon} alt="Add" className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* List Entity List - Scrollable */}
                            {(listEntities.length > 0 || quote?.pendingStorekeeperItems?.length > 0) && (
                                <div className="flex-1 overflow-y-auto space-y-[8px]">
                                    {listEntities.map((entity, index) => {
                                        const isProductLine = isProductEntity(entity);
                                        const unitPriceHT = (entity.unitPrice != null && entity.unitPrice !== undefined)
                                            ? entity.unitPrice
                                            : (entity.product?.priceHT ?? 0);
                                        // Services: 20% TVA by default; free line: value from input field
                                        const displayVatRate = isProductLine ? (entity.vatRate ?? 0) : 20;
                                        const taxRate = isProductLine
                                            ? (entity.vatRate != null && entity.vatRate !== undefined ? entity.vatRate / 100 : 0)
                                            : 0.20;
                                        const unitPriceTTC = unitPriceHT * (1 + taxRate);

                                        const productDiscount = entity.product?.discount || 0;
                                        const quoteDiscount = entity.discount || 0;

                                        const priceAfterProductDiscountHT = unitPriceHT * (1 - productDiscount / 100);
                                        const priceAfterProductDiscountTTC = unitPriceTTC * (1 - productDiscount / 100);

                                        const finalDiscountMultiplier = 1 - quoteDiscount / 100;
                                        const discountedPriceHTNum = priceAfterProductDiscountHT * finalDiscountMultiplier;
                                        const discountedPriceHT = discountedPriceHTNum.toFixed(2);
                                        const discountedPriceTTC = (priceAfterProductDiscountTTC * finalDiscountMultiplier).toFixed(2);

                                        const hasAnyDiscount = productDiscount > 0 || quoteDiscount > 0;
                                        const displayValueInInput = hasAnyDiscount ? discountedPriceHTNum : unitPriceHT;

                                        const displayProductName = isProductLine ? (entity.designation || entity.product?.name || "Produit") : (entity.product?.name || "Service");

                                        return (
                                            <div
                                                key={index}
                                                className="grid grid-cols-[24%_17%_9%_13%_13%_11%_10%_3%] items-center py-2 px-4 rounded-lg bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)] gap-0"
                                            >
                                                {/* Product Info */}
                                                <div className="flex items-center gap-2 w-full">
                                                    <div className="w-full">
                                                        <div className="flex items-center gap-2">
                                                            {entity.product?.discount > 0 && (
                                                                <p className="inline-flex items-center gap-0.5 text-[9px] bg-[#FFD700] rounded-[12px] px-1 py-0.5 font-bold text-[#043F88]">
                                                                    <img src={discountIcon} alt="discount" className="w-2 h-2" />
                                                                    <span>{entity.product?.discount}%</span>
                                                                </p>
                                                            )}
                                                        </div>
                                                        {isProductLine ? (
                                                            <div className="flex items-start gap-1.5 w-full">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setListEntities((prev) => prev.map((e, i) => i === index ? { ...e, notForProcurement: !e.notForProcurement } : e))}
                                                                    title={entity.notForProcurement ? 'À réparer — ne pas acheter' : 'Marquer comme à réparer (ne pas acheter)'}
                                                                    className={`flex-shrink-0 flex items-center justify-center w-5 h-5 mt-1 rounded-full transition-opacity ${entity.notForProcurement ? 'opacity-100' : 'opacity-25 hover:opacity-60'}`}
                                                                >
                                                                    <img src={repairIcon} alt="À réparer — ne pas acheter" className="w-3.5 h-3.5" />
                                                                </button>
                                                                <textarea
                                                                    rows={1}
                                                                    value={entity.designation || ''}
                                                                    onChange={(e) => {
                                                                        const value = e.target.value;
                                                                        setListEntities((prev) => prev.map((en, i) => i === index ? { ...en, designation: value } : en));
                                                                        e.target.style.height = 'auto';
                                                                        e.target.style.height = `${e.target.scrollHeight}px`;
                                                                    }}
                                                                    ref={(el) => {
                                                                        if (el) {
                                                                            el.style.height = 'auto';
                                                                            el.style.height = `${el.scrollHeight}px`;
                                                                        }
                                                                    }}
                                                                    className="flex-1 min-w-0 mr-3 text-center text-[#043F88] font-semibold text-[16px] border border-gray-200 rounded-lg px-1 py-1 focus:border-[#043F88] focus:outline-none resize-none overflow-hidden"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <p className="text-[#6B78F8] font-semibold text-[16px]">
                                                                {displayProductName}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Prix HT (editable only for free lines) */}
                                                <div className="text-left flex flex-col items-start justify-center min-w-0 w-full pr-1">
                                                    <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.priceuHT`).toUpperCase()}</p>
                                                    {isProductLine && entity.unitPrice == null ? (
                                                        <>
                                                            {(!entity.priceStatus || entity.priceStatus === 'NONE') && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => requestProposition(index)}
                                                                    disabled={propositionActionIndex === index}
                                                                    className="text-[#007BC6] text-[13px] font-semibold underline disabled:opacity-50 disabled:cursor-not-allowed flex items-start gap-1 text-left whitespace-normal break-words w-full"
                                                                >
                                                                    {propositionActionIndex === index && (
                                                                        <div className="w-3 h-3 border-2 border-[#007BC6] border-t-transparent rounded-full animate-spin"></div>
                                                                    )}
                                                                    Demander une proposition
                                                                </button>
                                                            )}
                                                            {entity.priceStatus === 'PENDING' && (
                                                                <span className="text-[#FEA36E] text-[13px] font-semibold flex items-center gap-1">
                                                                    <img src={loaderIcon} alt="En attente" className="w-3 h-3" />
                                                                    En attente
                                                                </span>
                                                            )}
                                                            {entity.priceStatus === 'ANSWERED' && (
                                                                <div className="flex flex-col items-start gap-0">
                                                                    <div className="flex items-center gap-1">
                                                                    <span className="text-[#0AD69F] text-[13px] font-semibold flex items-center gap-1">
                                                                        <img src={checkIcon} alt="En attente" className="w-4 h-4" />
                                                                        Proposé</span>
                                                                    <input
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        value={validatingPrice?.index === index
                                                                            ? Number(validatingPrice.value).toFixed(2)
                                                                            : Number(entity.proposedPrice ?? '').toFixed(2)}
                                                                        onFocus={() => {
                                                                            if (validatingPrice?.index !== index) {
                                                                                setValidatingPrice({ index, value: entity.proposedPrice != null ? String(entity.proposedPrice) : '' });
                                                                            }
                                                                        }}
                                                                        onChange={(e) => setValidatingPrice({ index, value: e.target.value.replace(',', '.') })}
                                                                        className="w-full max-w-[80px] text-center text-[#043F88] font-bold text-sm border border-gray-200 rounded-lg px-1 py-0.5 focus:border-[#043F88] focus:outline-none"
                                                                    />
                                                                    </div>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => validateProposition(index)}
                                                                        disabled={propositionActionIndex === index}
                                                                        className="text-[#007BC6] text-[13px] font-semibold underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                                                                    >
                                                                        {propositionActionIndex === index && (
                                                                            <div className="w-3 h-3 border-2 border-[#007BC6] border-t-transparent rounded-full animate-spin"></div>
                                                                        )}
                                                                        Valider
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </>
                                                    ) : isProductLine ? (
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={editingPrice?.index === index
                                                                ? editingPrice.value
                                                                : (displayValueInInput != null && displayValueInInput !== '' ? Number(displayValueInInput).toFixed(2) : '')}
                                                            onFocus={(e) => {
                                                                const formatted = displayValueInInput != null && displayValueInInput !== '' ? Number(displayValueInInput).toFixed(2) : '';
                                                                setEditingPrice({ index, value: formatted });
                                                                setTimeout(() => e.target.select(), 0);
                                                            }}
                                                            onChange={(e) => {
                                                                if (editingPrice?.index === index) {
                                                                    setEditingPrice({ index, value: e.target.value.replace(',', '.') });
                                                                }
                                                            }}
                                                            onBlur={() => {
                                                                if (editingPrice?.index !== index) return;
                                                                if (editingPrice.value.trim() === '') {
                                                                    setEditingPrice(null);
                                                                    return;
                                                                }
                                                                handleUnitPriceChange(
                                                                    index,
                                                                    editingPrice.value,
                                                                    hasAnyDiscount ? { productDiscount, quoteDiscount } : null
                                                                );
                                                                setEditingPrice(null);
                                                            }}
                                                            className="w-full max-w-[100px] text-center text-[#043F88] font-bold text-lg border border-gray-200 rounded-lg px-2 py-1 focus:border-[#043F88] focus:outline-none"
                                                        />
                                                    ) : (
                                                        <p className="text-[#043F88] font-bold text-lg">
                                                            {displayValueInInput != null && displayValueInInput !== '' ? Number(displayValueInInput).toFixed(2) : '—'}
                                                        </p>
                                                    )}
                                                    {hasAnyDiscount && (
                                                        <p className="text-[#ACAFC6] text-sm line-through mt-0.5">
                                                            {Number(unitPriceHT).toFixed(2)}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* TVA */}
                                                <div className="text-left">
                                                    <p className="text-[#707070] font-semibold text-[12px]">TVA (%)</p>
                                                    <p className="text-[#043F88] font-bold text-lg">{displayVatRate}%</p>
                                                </div>

                                                {/* Prix TTC */}
                                                <div className="text-left">
                                                    <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.priceuTTC`).toUpperCase()}</p>
                                                    <p className="text-[#043F88] font-bold text-lg">
                                                        {formatAmount(discountedPriceTTC)}
                                                    </p>
                                                    {hasAnyDiscount && (
                                                        <p className="text-[#ACAFC6] text-sm line-through inline-flex items-baseline">
                                                            {formatAmount(unitPriceTTC)}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Remise */}
                                                <div className="flex flex-col items-start">
                                                    <p className="text-[#707070] font-semibold text-[12px] mb-1">{t(`rows.remise%`)}</p>
                                                    <div className="flex items-stretch rounded-[12px] border border-[#fea26f] bg-[#fffaf8] divide-x divide-[#fea26f]">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDiscountChange(index, -0.1)}
                                                            className="w-8 h-8 flex items-center justify-center text-orange-500 font-bold"
                                                        >
                                                            <img src={minusY} alt="Minus" className="w-4 h-4" />
                                                        </button>
                                                        <span className="w-10 h-8 flex items-center justify-center text-[#38485C] font-semibold text-[14px]">
                                                            {entity.discount?.toFixed(1) || "0.0"}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleDiscountChange(index, 0.1)}
                                                            className="w-8 h-8 flex items-center justify-center text-orange-500 font-bold"
                                                        >
                                                            <img src={plusY} alt="Plus" className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>


                                                {/* Quantité */}
                                                <div className="flex flex-col items-start">
                                                    <p className="text-[#707070] font-semibold text-[12px] mb-1">{t(`rows.quantity`).toUpperCase()}</p>
                                                    <div className="flex items-stretch rounded-[12px] border border-[#6C78F8] bg-[#6C78F858] divide-x divide-[#6C78F8]">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const current = getEffectiveQuantity(entity);
                                                                const next = Math.round(Math.max(0, current - 1) * 100) / 100;
                                                                setListEntities((prev) => prev.map((e, i) => i === index
                                                                    ? (isService(e) ? { ...e, decimalQuantity: next } : { ...e, quantity: next })
                                                                    : e));
                                                            }}
                                                            className="w-8 h-8 flex items-center justify-center"
                                                        >
                                                            <img src={minus} alt="Minus" className="w-4 h-4" />
                                                        </button>
                                                        <input
                                                            type="text"
                                                            inputMode="decimal"
                                                            value={isService(entity) ? (entity.decimalQuantity ?? '') : (entity.quantity ?? '')}
                                                            onChange={(e) => {
                                                                if (isService(entity)) {
                                                                    handleQuantityChange(index, e.target.value);
                                                                    return;
                                                                }
                                                                const raw = e.target.value;
                                                                setListEntities((prev) => prev.map((en, i) => {
                                                                    if (i !== index) return en;
                                                                    if (raw === '') return { ...en, quantity: '' };
                                                                    const parsed = parseInt(raw, 10);
                                                                    return Number.isNaN(parsed) ? en : { ...en, quantity: parsed };
                                                                }));
                                                            }}
                                                            className="w-10 h-8 text-center font-semibold text-[#38485C] text-[14px] bg-transparent focus:outline-none"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                const current = getEffectiveQuantity(entity);
                                                                const next = Math.round((current + 1) * 100) / 100;
                                                                setListEntities((prev) => prev.map((e, i) => i === index
                                                                    ? (isService(e) ? { ...e, decimalQuantity: next } : { ...e, quantity: next })
                                                                    : e));
                                                            }}
                                                            className="w-8 h-8 flex items-center justify-center"
                                                        >
                                                            <img src={plus} alt="Plus" className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>


                                                {/* Prix Total */}
                                                <div className="text-left">
                                                    <p className="text-[#707070] font-semibold text-[12px]">{t(`rows.totalPrice`)}</p>
                                                    <p className="text-[#043F88] font-bold text-lg">
                                                        {formatAmount(getEffectiveQuantity(entity) * discountedPriceTTC)}
                                                    </p>
                                                    {hasAnyDiscount && (
                                                        <p className="text-[#ACAFC6] text-sm line-through inline-flex items-baseline">
                                                            {formatAmount(getEffectiveQuantity(entity) * unitPriceTTC)}
                                                        </p>
                                                    )}
                                                </div>

                                                {/* Delete Icon */}
                                                <div className="flex justify-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleTrashClick(index)}
                                                        className="w-8 h-8 flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                                                    >
                                                        <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                                                    </button>
                                                </div>

                                            </div>
                                        );
                                    })}

                                    {quote?.pendingStorekeeperItems?.length > 0 && (
                                        <div className="mt-4 pt-4 mb-2">
                                            <div className="flex items-center gap-1 mb-2">
                                                <h3 className="text-[14px] font-semibold text-[#3B3484]">AJOUTÉ PAR LE MAGASINIER</h3>
                                                <span className="text-[9px] font-semibold text-white bg-[#707070] rounded-[12px] px-1 py-0">
                                                    NON INCLUS DANS LE TOTAL
                                                </span>
                                            </div>
                                            <div className="space-y-2">
                                                {quote.pendingStorekeeperItems.map((item) => {
                                                    const edit = getPendingItemEdit(item);
                                                    return (
                                                    <div
                                                        key={item.deliveryNoteDetailId}
                                                        className="grid grid-cols-[28%_15%_8%_10%_10%_10%_9%_10%] items-center py-2 px-4 rounded-lg bg-[#F7F9FB] border border-dashed border-[#CDCBF3] gap-0"
                                                    >
                                                        <div>
                                                            <p className="text-[#707070] text-[11px] font-semibold">{item.product?.reference}</p>
                                                            <input
                                                                type="text"
                                                                value={edit.name}
                                                                onChange={(e) => updatePendingItemEdit(item, 'name', e.target.value)}
                                                                className="w-[80%] text-center text-[#6B78F8] font-semibold text-[14px] border border-gray-200 rounded-lg px-1 py-1 focus:border-[#6B78F8] focus:outline-none"
                                                            />
                                                        </div>
                                                        <div className="text-left">
                                                            <p className="text-[#707070] font-semibold text-[11px]">PRIX UN HT PROPOSÉ</p>
                                                            <input
                                                                type="number"
                                                                value={edit.unitPrice}
                                                                onChange={(e) => updatePendingItemEdit(item, 'unitPrice', e.target.value)}
                                                                className="w-[80%] text-center text-[#043F88] font-bold text-[14px] border border-gray-200 rounded-lg px-1 py-1 focus:border-[#043F88] focus:outline-none"
                                                            />
                                                        </div>
                                                        <div className="text-left">
                                                            <p className="text-[#707070] font-semibold text-[11px]">TVA</p>
                                                            <p className="text-[#043F88] font-bold text-[14px]">{item.vatRate ?? 0}%</p>
                                                        </div>
                                                        <div className="text-left">
                                                            <p className="text-[#707070] font-semibold text-[11px]">PRIX UN TTC</p>
                                                            <p className="text-[#043F88] font-bold text-[14px]">
                                                                {formatAmount(item.quantity ? (item.priceTTC / item.quantity) : item.priceTTC)}
                                                            </p>
                                                        </div>
                                                        <div className="text-left">
                                                            <p className="text-[#707070] font-semibold text-[11px]">QUANTITÉ</p>
                                                            <p className="text-[#043F88] font-bold text-[14px]">{item.quantity}</p>
                                                        </div>
                                                        <div className="text-left">
                                                            <p className="text-[#707070] font-semibold text-[11px]">PRIX TOTAL</p>
                                                            <p className="text-[#043F88] font-bold text-[14px]">{formatAmount(item.priceHT)}</p>
                                                        </div>
                                                        <div className="text-left min-w-0">
                                                            <p className="text-[#707070] font-semibold text-[11px]">BL</p>
                                                            <p className="text-[#38485C] font-semibold text-[11px] truncate" title={item.deliveryNoteNumber}>{item.deliveryNoteNumber}</p>
                                                        </div>
                                                        <div className="flex justify-end">
                                                            <button
                                                                type="button"
                                                                disabled={integratingId === item.deliveryNoteDetailId}
                                                                onClick={() => integrateStorekeeperItem(item)}
                                                                className="bg-primary text-white text-[12px] font-semibold rounded-[12px] px-2 py-1 disabled:opacity-50"
                                                            >
                                                                Intégrer
                                                            </button>
                                                        </div>
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                        </form>
                    )}
                </div>

                {/* Totals and Footer */}
                <div className="pl-6 pr-6 pt-2 pb-6 ">
                    <div className="flex justify-between items-center">
                        <div className="flex text-left">
                            <p className="text-[#231E29] text-[16px] font-medium">
                                ACOMPTE:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(formValues.advancePayment) || formatAmount("0.00")}
                            </p>
                            <p className="text-[#231E29] text-[16px] font-medium pl-[50px]">
                                TOTAL HT:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(totalHT)}
                            </p>
                            <p className="text-[#231E29] text-[16px] font-medium pl-[50px]">
                                TOTAL TTC:
                            </p>
                            <p className="text-[#043F88] text-[16px] font-bold pl-2">
                                {formatAmount(totalTTC)}
                            </p>
                        </div>
                        <div >
                            <SaveCancel
                                onSaveClick={handleSubmit}
                                onCancelClick={onCancel}
                                loading={submitting}
                                disabled={nothingChangedSinceIntegration}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                manual={snackbar.manual}
            />
        </div>
    );
};

export default QuoteForm;